import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import {
  errorMessageParser,
  HierarchicalQuestionService,
  ModalService,
  ShowError,
  ShowInfo
} from '@ui/legacy-lib';

import { CustomQuestionFacade } from '../../core/services/custom-question.facade';
import { OpenCompleteGarageModal } from '../property-matches/property-matches.actions';
import { CustomQuestionsModalComponent } from '../../components/custom-questions-modal/custom-questions-modal.component';
import { notificationConfig } from '../../config/notification.config';
import { CustomQuestionsState } from './custom-questions.reducers';
import { isHierarchicalQuestionsModalOpen } from './custom-questions.selectors';
import {
  HIERARCHICAL_ROOT_QUESTION_RESPONSE,
  HierarchicalQuestionResponse,
  HierarchicalQuestionResponseFail,
  HierarchicalQuestionResponseSuccess,
  LOAD_CUSTOM_QUESTIONS,
  LOAD_QUESTIONS_AND_ANSWERS_BY_CUSTOMER,
  LOAD_QUESTIONS_BY_CUSTOMER,
  LoadCustomQuestions,
  LoadCustomQuestionsFail,
  LoadCustomQuestionsSuccess,
  LoadCustomerQuestionsSuccess,
  LoadHierarchicalQuestionsSuccess,
  LoadQuestionsAndAnswersByCustomer,
  LoadQuestionsAndAnswersByCustomerSuccess,
  LoadQuestionsByCustomer,
  OPEN_CUSTOM_QUESTIONS_MODAL,
  OpenCustomQuestionsModal,
  SAVE_CUSTOM_QUESTIONS_ANSWERS,
  SaveCustomQuestionsAnswers,
  SaveCustomQuestionsAnswersFail,
  SaveCustomQuestionsAnswersSuccess
} from './custom-questions.actions';

@Injectable()
export class CustomQuestionsEffects {
  private store = inject<Store<CustomQuestionsState>>(Store);
  private actions$ = inject(Actions);
  private customQuestionFacade = inject(CustomQuestionFacade);
  saveCustomQuestionAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveCustomQuestionsAnswers>(SAVE_CUSTOM_QUESTIONS_ANSWERS),
      switchMap(({ answers }) =>
        this.customQuestionFacade.saveAnswers(answers).pipe(
          map(() => new SaveCustomQuestionsAnswersSuccess()),
          catchError((err: Error) => [
            new SaveCustomQuestionsAnswersFail(err),
            new ShowError(notificationConfig.customQuestions.answer.error)
          ])
        )
      )
    )
  );
  hierarchicalRootQuestionResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HierarchicalQuestionResponse>(HIERARCHICAL_ROOT_QUESTION_RESPONSE),
      switchMap(({ response }) =>
        this.customQuestionFacade
          .hierarchicalRootQuestionResponse(response)
          .pipe(
            mergeMap(rootQuestions => [
              new HierarchicalQuestionResponseSuccess(rootQuestions),
              new ShowInfo(notificationConfig.customQuestions.answer.success)
            ]),
            catchError(error => [
              new HierarchicalQuestionResponseFail(
                new Error(errorMessageParser(error))
              )
            ])
          )
      )
    )
  );
  private hierarchicalQuestionService = inject(HierarchicalQuestionService);
  loadCustomQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCustomQuestions>(LOAD_CUSTOM_QUESTIONS),
      switchMap(({ id }) =>
        this.customQuestionFacade.getCustomQuestions(`${id}`).pipe(
          mergeMap(customQuestionContainer => [
            new LoadCustomQuestionsSuccess(
              customQuestionContainer.customQuestions
            ),
            new LoadHierarchicalQuestionsSuccess(
              this.hierarchicalQuestionService.getAnsweredRootQuestions(
                customQuestionContainer.questions
              )
            )
          ]),
          catchError(err => [
            new LoadCustomQuestionsFail(err),
            new ShowError(notificationConfig.customQuestions.questions.error)
          ])
        )
      )
    )
  );

  loadQuestionByCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadQuestionsByCustomer>(LOAD_QUESTIONS_BY_CUSTOMER),
      switchMap(({ id }) =>
        this.customQuestionFacade.getQuestionsByCustomer(`${id}`).pipe(
          mergeMap(customQuestionContainer => [
            new LoadCustomQuestionsSuccess(
              customQuestionContainer.customQuestions
            ),
            new LoadHierarchicalQuestionsSuccess(
              this.hierarchicalQuestionService.getAnsweredRootQuestions(
                customQuestionContainer.questions
              )
            ),
            new LoadCustomerQuestionsSuccess(customQuestionContainer.questions)
          ]),
          catchError(err => [
            new LoadCustomQuestionsFail(err),
            new ShowError(notificationConfig.customQuestions.questions.error)
          ])
        )
      )
    )
  );

  loadQuestionsAndAnswersByCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadQuestionsAndAnswersByCustomer>(
        LOAD_QUESTIONS_AND_ANSWERS_BY_CUSTOMER
      ),
      switchMap(({ id }) =>
        this.customQuestionFacade
          .getQuestionsAndAnswersByCustomer(`${id}`)
          .pipe(
            mergeMap(customQuestionContainer => [
              new LoadQuestionsAndAnswersByCustomerSuccess(
                this.hierarchicalQuestionService.getAnsweredRootQuestions(
                  customQuestionContainer.questions
                )
              )
            ]),
            catchError(err => [
              new LoadCustomQuestionsFail(err),
              new ShowError(notificationConfig.customQuestions.questions.error)
            ])
          )
      )
    )
  );
  private modalService = inject(ModalService);
  openCustomQuestionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenCustomQuestionsModal>(OPEN_CUSTOM_QUESTIONS_MODAL),
        withLatestFrom(this.store.select(isHierarchicalQuestionsModalOpen)),
        tap(
          ([
            { propertyMatchBean, openSecondModalForGarageProperties },
            isOpen
          ]) => {
            const questions = propertyMatchBean.questionContainer?.questions
              ? this.hierarchicalQuestionService.getAnsweredRootQuestions(
                  propertyMatchBean.questionContainer.questions
                )
              : [];

            if (isOpen) return;

            if (
              !questions?.length &&
              !propertyMatchBean.questionContainer?.customQuestions?.length
            ) {
              if (!openSecondModalForGarageProperties) return;

              this.store.dispatch(OpenCompleteGarageModal());
            }

            this.modalService.open(CustomQuestionsModalComponent, {
              backdrop: 'static',
              keyboard: false,
              data: {
                propertyMatchBean,
                questionContainer: {
                  ...propertyMatchBean.questionContainer,
                  questions
                },
                openSecondModalForGarageProperties
              }
            });
          }
        )
      ),
    { dispatch: false }
  );
}
